import { Alert, AlertTitle } from '@mui/material';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useLocation, useSearch } from 'react-location';
// import FeedbackForm from '../components/feedback/FeedbackForm';
import TurnArrived from '../components/TurnArrived';
import ChatComponent from '../components/waiting-room/ChatComponent';
import Header from '../components/waiting-room/Header';
import HelpResources from '../components/waiting-room/HelpResources';
import HotlineTabpanel from '../components/waiting-room/HotlineTabpanel';
import WaitingContainer from '../components/waiting-room/WaitingContainer';
import useGetQueuePosition from '../modules/datastores/useGetQueuePosition';
import useGetSurvivorsConversation from '../modules/datastores/useGetSurvivorsConversation';
import { useFetchHelpRoom } from '../modules/datastores/useHelproom';
import { HOC } from '../modules/hocs/googleCaptcha';
import { SocketProvider } from '../modules/prelude/context/socket';
import { FederatedHelproomStatus } from '../components/waiting-room/WaitingRoomHelproom';
import { Reconnect } from '../components/Reconnect';
import FeedbackForm from '../components/feedback/FeedbackForm';

/**
 * This is the Main Page - it should be visisble only if Chat Key is there.
 */

export interface AdditionalProps {
  additionalProp?: {
    captcha_enabled?: string;
    captcha_token?: string;
  };
}

const Root: React.FC<AdditionalProps> = (props) => {
  const query = useSearch();
  const messaging_service_key = query['messaging_service_key'] as string;
  const task_sid = query['task_sid'] as string;
  const queuePositionQuery = useGetQueuePosition(
    messaging_service_key,
    task_sid,
    { 'g-recaptcha-response': props?.additionalProp?.captcha_token ? props?.additionalProp?.captcha_token : '' },
    props?.additionalProp?.captcha_enabled ? props?.additionalProp?.captcha_enabled : ''
  );
  const chatSession = useGetSurvivorsConversation();
  const routerLocation = useLocation();
  const helproomInfo = useFetchHelpRoom(queuePositionQuery.data?.settings?.webchat_helproom_id);
  if (!queuePositionQuery.isLoading && !queuePositionQuery.isError && queuePositionQuery.data && props?.additionalProp?.captcha_enabled === 'false' && !chatSession.cancelled) {
    return (
      <SocketProvider queue_sid={queuePositionQuery.data.queue_sid} task_sid={task_sid} messaging_service_key={messaging_service_key} captchaToken={props?.additionalProp?.captcha_token}>
        <Reconnect />
        <Box className="px-4 md:px-14 py-8 custom-hotline-page-design">
          <Header
            task_sid={task_sid}
            messaging_service_key={messaging_service_key}
            helproom_session_on={chatSession.helproom_position_available}
            helproom_session_data={{
              visitor_id: chatSession.visitor_id,
              helproom_id: chatSession.helproom_id,
            }}
            helproom_id={queuePositionQuery.data?.settings?.webchat_helproom_id}
            online_hotline_on={!chatSession.ended}
            captcha_token={props?.additionalProp?.captcha_token ? props?.additionalProp?.captcha_token : ''}
            webchat_language={queuePositionQuery.data?.settings?.webchat_language}
          />
          {chatSession.turn_arrived_in_hotlines && <TurnArrived captcha_token={props?.additionalProp?.captcha_token} />}
          {(chatSession.started || queuePositionQuery.data?.position === -1) && (
            <ChatComponent
              chatSession={chatSession}
              task_sid={task_sid}
              messaging_service_key={messaging_service_key}
              captcha_token={props?.additionalProp?.captcha_token ? props?.additionalProp?.captcha_token : ''}
              webchat_postassessment_no_helproom={queuePositionQuery.data.settings.webchat_postassessment_no_helproom}
              webchat_postassessment_indirect_helproom={queuePositionQuery.data.settings.webchat_postassessment_indirect}
              helproom_exited={chatSession.helproom_completed}
              webchat_language={queuePositionQuery.data?.settings?.webchat_language}
            />
          )}

          {queuePositionQuery.data?.position > -1 && !chatSession.started && queuePositionQuery.data && !chatSession.helproom_position_available && (
            <>
              <WaitingContainer
                task_sid={task_sid}
                waiting_via_sms={chatSession.waiting_via_sms}
                messaging_service_key={messaging_service_key}
                queuePosition={queuePositionQuery.data?.position}
                wait_via_sms_enabled={queuePositionQuery.data?.settings?.webchat_smsenabled}
                webchat_language={queuePositionQuery.data?.settings?.webchat_language}
                wait_via_sms_data={queuePositionQuery?.data?.wait_via_sms_data} 
                isSMSOptInProcessActive={ queuePositionQuery?.data?.isSMSOptInProcessActive}
              />
              <HelpResources
                queueSettings={queuePositionQuery.data?.settings}
                helproom_open={helproomInfo.data?.available}
                helproom={helproomInfo.data}
                task_sid={task_sid}
                captcha_token={props?.additionalProp?.captcha_token}
                captcha_enabled={props?.additionalProp?.captcha_enabled}
              />
            </>
          )}

          {queuePositionQuery.data?.position > -1 && !chatSession.started && queuePositionQuery.data && chatSession.helproom_position_available && helproomInfo.isSuccess && (
            <FederatedHelproomStatus
              unique_identity={task_sid}
              helproom={helproomInfo.data}
              captcha_token={props?.additionalProp?.captcha_token}
              captcha_enabled={props?.additionalProp?.captcha_enabled}
            />
          )}
          {chatSession.helproom_completed && !chatSession.started && queuePositionQuery.data?.position > -1 && (
            <FeedbackForm
              task_sid={task_sid}
              dialog_open={chatSession.helproom_completed}
              form_id={helproomInfo.data?.components.helproom_postassessment || ''}
              webchat_language={queuePositionQuery.data?.settings?.webchat_language}
            />
          )}
          <HotlineTabpanel webchat_language={queuePositionQuery.data?.settings?.webchat_language} />
        </Box>
      </SocketProvider>
    );
  } else if (queuePositionQuery.isError) {
    return (
      <Alert severity="error">
        <AlertTitle onClick={() => routerLocation.history.back()}>{queuePositionQuery.error?.errors?.toString()}</AlertTitle>
      </Alert>
    );
  } else if (chatSession.cancelled) {
    return (
      <Dialog open={chatSession.cancelled} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">Something Went Wrong, or your task just got cancelled.</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You have been removed from the waitlist. If you did not cancel your place in line, please re-join. We are sorry for any inconvenience.{' '}
          </DialogContentText>
        </DialogContent>
      </Dialog>
    );
  } else {
    return <CircularProgress color="primary" />;
  }
};

export default HOC(Root);
